import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Truck, CheckCircle2, Download, Plus, X, Upload, Trash2, ImageIcon, Pencil, RotateCcw, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EmailCampaignTab } from "@/pages/EmailCampaignTab";
import { SmsCampaignTab } from "@/pages/SmsCampaignTab";
import { DiscountsTab } from "@/pages/DiscountsTab";
import { AIStudioTab } from "@/pages/AIStudioTab";
import { RichTextEditor } from "@/components/RichTextEditor";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["processing", "shipped", "delivered"];
const NOTE_COLORS = [
  { label: "Red", value: "#9a3b3b" },
  { label: "Maroon", value: "#560216" },
  { label: "Green", value: "#3a6b45" },
  { label: "Amber", value: "#b45309" },
  { label: "Gold", value: "#8a6d3b" },
  { label: "Gray", value: "#737373" },
];
const STATUS_STYLE = {
  processing: "bg-[#Eadfce] text-[#8a6d3b]",
  shipped: "bg-[#dfe6ea] text-[#3a5a6b]",
  delivered: "bg-[#dfeade] text-[#3a6b45]",
};

const CUR_SYMBOL = { CAD: "$", USD: "$", EUR: "€", GBP: "£", AUD: "$" };

const OrderInvoice = ({ order, open, onOpenChange }) => {
  const cur = order.currency || "CAD";
  const rate = Number(order.fx_rate || 1);
  const sym = CUR_SYMBOL[cur] || "$";
  const inCur = (cad) => `${sym}${(Number(cad || 0) * rate).toFixed(2)} ${cur}`;
  const inCad = (cad) => `$${Number(cad || 0).toFixed(2)} CAD`;
  const dual = (cad) => (cur === "CAD" ? inCad(cad) : `${inCur(cad)} · ${inCad(cad)}`);
  const a = order.shipping_address || {};
  const subtotal = Number(order.subtotal || 0);
  const discount = Number(order.discount_amount || 0);
  const shipping = Number(order.shipping || 0);
  const tax = Number(order.tax || 0);
  const total = Number(order.cad_amount != null ? order.cad_amount : order.amount || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`invoice-dialog-${order.order_id}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Invoice — Order #{order.order_id}</DialogTitle>
          <DialogDescription className="sr-only">Full order invoice with items, totals and shipping details.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          <div className="flex flex-wrap justify-between gap-4 text-xs text-[#737373]">
            <span>{order.captured_at ? new Date(order.captured_at).toLocaleString() : ""}</span>
            <span className="uppercase tracking-[0.15em]">{order.fulfillment_status || "processing"}{order.refunded ? " · Refunded" : ""}</span>
          </div>

          {/* Customer + shipping */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="eyebrow mb-2">Customer</p>
              <p data-testid="invoice-email">{order.user_email || a.name || "—"}</p>
              <p className="text-[#5a5a5a]" data-testid="invoice-phone">{a.phone || order.phone || "No phone provided"}</p>
            </div>
            <div>
              <p className="eyebrow mb-2">Shipping Address</p>
              <div className="text-[#3a3a3a] leading-relaxed" data-testid="invoice-address">
                {a.name && <div>{a.name}</div>}
                {a.address && <div>{a.address}</div>}
                <div>{[a.city, a.province, a.postal_code].filter(Boolean).join(", ")}</div>
                {a.country && <div>{a.country}</div>}
              </div>
            </div>
          </div>

          {order.order_notes && (
            <div className="bg-[#faf8f5] border border-[#E5E5E5] rounded-md p-4" data-testid="invoice-order-notes">
              <p className="eyebrow mb-2">Order Notes from Customer</p>
              <p className="text-[#3a3a3a] leading-relaxed whitespace-pre-wrap">{order.order_notes}</p>
            </div>
          )}

          {/* Items */}
          <div>
            <p className="eyebrow mb-3">Items</p>
            <div className="space-y-3">
              {(order.line_items || []).map((li, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-[#eee] pb-3" data-testid={`invoice-item-${i}`}>
                  {li.image
                    ? <img src={li.image} alt={li.name} className="h-14 w-14 object-cover rounded-md bg-beige shrink-0" />
                    : <div className="h-14 w-14 rounded-md bg-beige shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{li.name}</p>
                    <p className="text-xs text-[#737373]">
                      Qty {li.quantity}{li.color ? ` · ${li.color}` : ""}{li.size ? ` · ${li.size}` : ""} · {inCur(li.price)} each
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div>{inCur(li.price * li.quantity)}</div>
                    {cur !== "CAD" && <div className="text-xs text-[#737373]">{inCad(li.price * li.quantity)}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="space-y-2 border-t border-[#d8d1c4] pt-4">
            <div className="flex justify-between"><span className="text-[#5a5a5a]">Subtotal</span><span data-testid="invoice-subtotal">{dual(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-[#3a6b45]"><span>Discount{order.discount_code ? ` (${order.discount_code})` : ""}</span><span data-testid="invoice-discount">−{dual(discount)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[#5a5a5a]">Shipping</span><span data-testid="invoice-shipping">{shipping > 0 ? dual(shipping) : "Free"}</span></div>
            <div className="flex justify-between"><span className="text-[#5a5a5a]">Tax</span><span data-testid="invoice-tax">{dual(tax)}</span></div>
            {Number(order.duties || 0) > 0 && (
              <div className="flex justify-between"><span className="text-[#5a5a5a]">Duties</span><span data-testid="invoice-duties">{dual(Number(order.duties))}</span></div>
            )}
            <div className="flex justify-between text-lg pt-2 border-t border-[#d8d1c4]"><span>Total</span><span data-testid="invoice-total">{dual(total)}</span></div>
            {cur !== "CAD" && (
              <p className="text-xs text-[#737373] pt-1">Charged {inCur(total)} at an exchange rate of 1 CAD = {rate.toFixed(4)} {cur}.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const OrderRow = ({ order, onSaved }) => {
  const [status, setStatus] = useState(order.fulfillment_status || "processing");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [shipMsg, setShipMsg] = useState(order.ship_message || "");
  const [stallionUrl, setStallionUrl] = useState(order.stallion_url || "");
  const [courierUrl, setCourierUrl] = useState(order.courier_url || "");
  const [saving, setSaving] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmt, setRefundAmt] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const orderTotal = Number(order.amount || 0);
  const alreadyRefunded = Number(order.refund_amount || 0);
  const remaining = Math.max(0, Math.round((orderTotal - alreadyRefunded) * 100) / 100);
  const fullyRefunded = !!order.refunded;
  const partiallyRefunded = !fullyRefunded && alreadyRefunded > 0;
  const currency = order.currency || "CAD";

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/orders/${order.order_id}`, {
        fulfillment_status: status,
        tracking_number: tracking || null,
        carrier: carrier || null,
        ship_message: shipMsg || null,
        stallion_url: stallionUrl || null,
        courier_url: courierUrl || null,
      });
      toast.success(status === "shipped" ? "Marked shipped — customer emailed." : "Order updated.");
      onSaved(res.data);
    } catch {
      toast.error("Could not update order.");
    } finally {
      setSaving(false);
    }
  };

  const openRefund = () => {
    setRefundAmt(remaining.toFixed(2));
    setRefundOpen(true);
  };

  const submitRefund = async () => {
    const amt = parseFloat(refundAmt);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (amt > remaining + 0.001) { toast.error(`Max refundable is $${remaining.toFixed(2)}.`); return; }
    setRefunding(true);
    try {
      const isFull = Math.abs(amt - remaining) < 0.01;
      const res = await api.post(`/admin/orders/${order.order_id}/refund`, { amount: isFull ? null : amt });
      toast.success(`Refunded $${amt.toFixed(2)} ${currency}.`);
      onSaved(res.data);
      setRefundOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail || (e?.response ? "Refund failed. Please check the order in your PayPal dashboard." : "Couldn't reach the server. Check your connection and verify the refund in PayPal before retrying.");
      toast.error(msg, { duration: 8000 });
    } finally {
      setRefunding(false);
    }
  };

  const statusBadge = fullyRefunded
    ? "bg-[#f3d9d9] text-[#9a3b3b]"
    : (STATUS_STYLE[order.fulfillment_status] || STATUS_STYLE.processing);
  const statusLabel = fullyRefunded ? "Refunded" : (order.fulfillment_status || "processing");

  return (
    <div className={`border rounded-sm p-6 ${fullyRefunded ? "border-[#e2b8b8] bg-[#fdf5f5]" : "border-[#E5E5E5]"}`} data-testid={`admin-order-${order.order_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[#737373]">Order #{order.order_id}</p>
          <p className="text-sm mt-1">{order.user_email}</p>
          <p className="text-xs text-[#737373] mt-1">
            {order.captured_at ? new Date(order.captured_at).toLocaleString() : ""} · ${orderTotal.toFixed(2)} {currency}
          </p>
          {partiallyRefunded && (
            <p className="text-xs text-[#9a3b3b] mt-1" data-testid={`partial-refund-${order.order_id}`}>
              Partially refunded: ${alreadyRefunded.toFixed(2)} · ${remaining.toFixed(2)} remaining
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${statusBadge}`} data-testid={`order-status-badge-${order.order_id}`}>
            {statusLabel}
          </span>
          <button onClick={() => setInvoiceOpen(true)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#560216] hover:underline" data-testid={`view-invoice-${order.order_id}`}>
            <FileText strokeWidth={1.25} className="h-4 w-4" /> View Invoice
          </button>
        </div>
      </div>

      <OrderInvoice order={order} open={invoiceOpen} onOpenChange={setInvoiceOpen} />

      <div className="mt-4 flex flex-wrap gap-2 text-sm text-[#4a4a4a]">
        {(order.line_items || []).map((li, i) => (
          <span key={i} className="bg-beige px-3 py-1 rounded-full text-xs">{li.name} × {li.quantity}{li.color ? ` · ${li.color}` : ""}</span>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <label className="eyebrow block mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black bg-white" data-testid={`status-select-${order.order_id}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Carrier</label>
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. DHL" className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Tracking #</label>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number" className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid={`tracking-input-${order.order_id}`} />
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full disabled:opacity-60" data-testid={`save-order-${order.order_id}`}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="mt-3">
        <label className="eyebrow block mb-1">Message to customer <span className="text-[#a8a29a] normal-case tracking-normal">optional · shown at the top of the shipped email</span></label>
        <textarea value={shipMsg} onChange={(e) => setShipMsg(e.target.value)} rows={2} placeholder="e.g. Thank you for your patience — your order has now shipped and should arrive within 5–7 days." className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid={`ship-message-${order.order_id}`} />
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="eyebrow block mb-1">Stallion tracking link <span className="text-[#a8a29a] normal-case tracking-normal">optional</span></label>
          <input value={stallionUrl} onChange={(e) => setStallionUrl(e.target.value)} placeholder="https://stallionexpress.ca/tracking/..." className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid={`stallion-url-${order.order_id}`} />
        </div>
        <div>
          <label className="eyebrow block mb-1">Courier tracking link <span className="text-[#a8a29a] normal-case tracking-normal">optional</span></label>
          <input value={courierUrl} onChange={(e) => setCourierUrl(e.target.value)} placeholder="e.g. Canada Post / UPS tracking URL" className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid={`courier-url-${order.order_id}`} />
        </div>
      </div>
      <p className="mt-1.5 text-xs text-[#a8a29a]">Each link becomes a button in the shipped email — "Track your order with Stallion" and "Track your order with your courier". Buttons only appear if a link is provided.</p>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-[#737373]">Setting status to <strong>Shipped</strong> emails the customer their tracking details.</p>
        {!fullyRefunded && (
          <button onClick={openRefund} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#9a3b3b] hover:text-[#560216] transition-colors" data-testid={`refund-button-${order.order_id}`}>
            <RotateCcw strokeWidth={1.25} className="h-4 w-4" /> {partiallyRefunded ? "Refund More" : "Refund"}
          </button>
        )}
      </div>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent data-testid={`refund-dialog-${order.order_id}`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Refund Order #{order.order_id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5a5a5a]">
              Order total: <strong>${orderTotal.toFixed(2)} {currency}</strong>
              {alreadyRefunded > 0 && <> · Already refunded: <strong>${alreadyRefunded.toFixed(2)}</strong></>}
              <br />Refundable balance: <strong>${remaining.toFixed(2)}</strong>
            </p>
            <div>
              <label className="eyebrow block mb-1">Refund amount ({currency})</label>
              <input
                type="number" step="0.01" min="0" max={remaining}
                value={refundAmt}
                onChange={(e) => setRefundAmt(e.target.value)}
                className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black"
                data-testid={`refund-amount-input-${order.order_id}`}
              />
              <p className="text-xs text-[#737373] mt-1">Defaults to the full refundable amount. Lower it to issue a partial refund.</p>
            </div>
            <div className="bg-[#fdf5f5] border border-[#e2b8b8] rounded-md p-3 text-sm text-[#9a3b3b]">
              This sends real money back to the customer via PayPal and <strong>cannot be undone</strong>.
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button onClick={submitRefund} disabled={refunding} className="btn-primary inline-flex items-center disabled:opacity-50" data-testid={`confirm-refund-${order.order_id}`}>
                <RotateCcw strokeWidth={1.25} className="h-4 w-4 mr-2" /> {refunding ? "Processing…" : `Refund $${(parseFloat(refundAmt) || 0).toFixed(2)}`}
              </button>
              <button onClick={() => setRefundOpen(false)} disabled={refunding} className="btn-secondary" data-testid={`cancel-refund-${order.order_id}`}>Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InventoryRow = ({ product, onSaved, onDeleted }) => {
  const [stock, setStock] = useState(product.stock);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const hasSizeStock = product.size_stock && Object.keys(product.size_stock).length > 0;
  const dirty = String(stock) !== String(product.stock);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/products/${product.slug}`, { stock: Number(stock) });
      toast.success(`${product.name} stock updated to ${res.data.stock}.`);
      onSaved(res.data);
    } catch {
      toast.error("Could not update stock.");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${product.slug}`);
      toast.success(`${product.name} removed.`);
      onDeleted(product.slug);
    } catch {
      toast.error("Could not delete product.");
    }
  };

  const level = product.stock === 0 ? "out" : product.stock <= 10 ? "low" : "ok";
  const badge = { out: "bg-[#f2dede] text-[#9a3b3b]", low: "bg-[#Eadfce] text-[#b45309]", ok: "bg-[#dfeade] text-[#3a6b45]" }[level];
  const label = product.stock === 0 ? "Out of stock" : product.stock <= 10 ? `Low · ${product.stock}` : `In stock · ${product.stock}`;

  return (
    <div className="flex items-center gap-4 border border-[#E5E5E5] rounded-sm p-4" data-testid={`inventory-row-${product.slug}`}>
      {product.images?.[0] && <img src={product.images[0]} alt={product.name} className="h-16 w-12 object-cover rounded-sm bg-beige shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="font-serif text-lg leading-tight truncate">{product.name}</p>
        <p className="text-xs text-[#737373]">{product.category_label} · ${product.price.toFixed(2)} CAD</p>
        {hasSizeStock && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {Object.entries(product.size_stock).map(([s, n]) => (
              <span key={s} className={`text-[10px] px-2 py-0.5 rounded-full ${n === 0 ? "bg-[#f2dede] text-[#9a3b3b]" : "bg-beige text-[#5a5a5a]"}`}>{s}: {n}</span>
            ))}
          </div>
        )}
      </div>
      <span className={`hidden sm:inline text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full ${badge}`}>{label}</span>
      {hasSizeStock ? (
        <span className="text-xs text-[#737373] hidden md:inline">Edit sizes →</span>
      ) : (
        <>
          <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)}
            className="w-20 border border-[#E5E5E5] rounded-md px-3 py-2 text-sm text-center outline-none focus:ring-1 focus:ring-black" data-testid={`stock-input-${product.slug}`} />
          <button onClick={save} disabled={saving || !dirty} className="btn-primary px-5 py-2.5 disabled:opacity-40" data-testid={`save-stock-${product.slug}`}>
            {saving ? "…" : "Save"}
          </button>
        </>
      )}
      <button onClick={() => setEditing(true)} aria-label="Edit product" className="h-10 px-4 grid place-items-center rounded-full border border-[#E5E5E5] hover:border-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.12em] gap-1 flex items-center" data-testid={`edit-product-${product.slug}`}>
        <Pencil strokeWidth={1.25} className="h-3.5 w-3.5" /> Edit
      </button>
      <button onClick={del} aria-label="Delete product" className="h-10 w-10 grid place-items-center rounded-full border border-[#E5E5E5] hover:border-[#9a3b3b] hover:text-[#9a3b3b] transition-colors" data-testid={`delete-product-${product.slug}`}>
        <Trash2 strokeWidth={1} className="h-4 w-4" />
      </button>
      <EditProductModal product={product} open={editing} onOpenChange={setEditing} onSaved={onSaved} />
    </div>
  );
};

const CATEGORIES = [
  { value: "solid-modal", label: "Solid Modal" },
  { value: "print-modal", label: "Print Modal" },
  { value: "chiffon", label: "Chiffon" },
  { value: "accessories", label: "Accessories" },
];

const emptyForm = { name: "", price: "", compare_at_price: "", category: "solid-modal", material: "Modal", stock: "0", description: "" };

const DEFAULT_DETAILS = [
  "100% Modal",
  "Semi-transparent",
  "Lightweight & breathable",
  "Soft, fluid drape",
  "Ethically produced",
  "Hand wash only",
];

const ProductForm = ({ initial, submitLabel, onSubmit }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? {
          name: initial.name || "",
          price: String(initial.price ?? ""),
          compare_at_price: initial.compare_at_price != null ? String(initial.compare_at_price) : "",
          category: initial.category || "solid-modal",
          material: initial.material || "Modal",
          stock: String(initial.stock ?? 0),
          description: initial.description || "",
        }
      : emptyForm,
  );
  const [colors, setColors] = useState(
    initial?.colors?.length ? initial.colors.map((c) => ({ name: c.name, hex: c.hex })) : [{ name: "", hex: "#D8C3A5" }],
  );
  const initSizes = initial ? initial.sizes || [] : ["LIRA Mini", "LIRA Standard"];
  const [sizePresets, setSizePresets] = useState({
    "LIRA Mini": initSizes.includes("LIRA Mini"),
    "LIRA Standard": initSizes.includes("LIRA Standard"),
  });
  const [customSizes, setCustomSizes] = useState(initSizes.filter((s) => s !== "LIRA Mini" && s !== "LIRA Standard"));
  const [sizeStock, setSizeStock] = useState(initial?.size_stock || {});
  const [details, setDetails] = useState(
    initial
      ? (initial.details?.length ? initial.details : (initial.details_set ? [] : [...DEFAULT_DETAILS]))
      : [...DEFAULT_DETAILS],
  );
  const [images, setImages] = useState(initial?.images || []);
  const [preOrder, setPreOrder] = useState(initial?.pre_order || false);
  const [preOrderMsg, setPreOrderMsg] = useState(initial?.pre_order_message || "");
  const [notices, setNotices] = useState(initial?.notices?.length ? initial.notices.map((n) => ({ text: n.text || "", color: n.color || NOTE_COLORS[0].value })) : []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildSizes = () => {
    const out = [];
    if (sizePresets["LIRA Mini"]) out.push("LIRA Mini");
    if (sizePresets["LIRA Standard"]) out.push("LIRA Standard");
    customSizes.forEach((s) => { if (s.trim()) out.push(s.trim()); });
    return out;
  };
  const currentSizes = buildSizes();
  const perSize = currentSizes.length >= 2;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, r.data.url]);
      } catch {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const resetAll = () => {
    setForm(emptyForm); setColors([{ name: "", hex: "#D8C3A5" }]);
    setSizePresets({ "LIRA Mini": true, "LIRA Standard": true }); setCustomSizes([]);
    setSizeStock({}); setImages([]); setDetails([...DEFAULT_DETAILS]); setPreOrder(false); setPreOrderMsg(""); setNotices([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length === 0) { toast.error("Please upload at least one image."); return; }
    setSaving(true);
    try {
      const sizes = currentSizes;
      const payload = {
        name: form.name,
        price: parseFloat(form.price),
        compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
        category: form.category,
        material: form.material,
        description: form.description || null,
        colors: colors.filter((c) => c.name.trim()),
        sizes,
        images,
        details: details.map((d) => d.trim()).filter(Boolean),
        pre_order: preOrder,
        pre_order_message: preOrder ? (preOrderMsg.trim() || null) : null,
        notices: notices.map((n) => ({ text: n.text.trim(), color: n.color })).filter((n) => n.text),
      };
      if (perSize) {
        payload.size_stock = {};
        sizes.forEach((s) => { payload.size_stock[s] = parseInt(sizeStock[s] || "0", 10) || 0; });
      } else {
        payload.stock = parseInt(form.stock || "0", 10);
      }
      await onSubmit(payload, resetAll);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save product.");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full border border-[#E5E5E5] rounded-md px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-black";

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-8" data-testid={isEdit ? "edit-product-form" : "add-product-form"}>
      {/* Images */}
      <div>
        <label className="eyebrow block mb-3">Product Images</label>
        <div className="flex flex-wrap gap-3">
          {images.map((url, i) => (
            <div key={i} className="relative h-28 w-24 rounded-sm overflow-hidden bg-beige">
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-white/90">
                <X strokeWidth={1.5} className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <label className="h-28 w-24 border border-dashed border-[#c9c2b6] rounded-sm grid place-items-center cursor-pointer hover:bg-beige transition-colors" data-testid="upload-image-label">
            <div className="flex flex-col items-center gap-1 text-[#737373]">
              <Upload strokeWidth={1} className="h-5 w-5" />
              <span className="text-[10px] uppercase tracking-wider">{uploading ? "…" : "Upload"}</span>
            </div>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="upload-image-input" />
          </label>
        </div>
        <p className="text-xs text-[#737373] mt-2">First image is the main photo; the second shows on hover. JPG/PNG/WEBP.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="eyebrow block mb-2">Name</label>
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} placeholder="e.g. Amara Blush Modal" data-testid="product-name" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Category</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className={`${field} bg-white`} data-testid="product-category">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-2">Price ($ CAD)</label>
          <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className={field} placeholder="21.00" data-testid="product-price" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Compare-at Price ($) <span className="text-[#a8a29a] normal-case tracking-normal">optional</span></label>
          <input type="number" step="0.01" min="0" value={form.compare_at_price} onChange={(e) => set("compare_at_price", e.target.value)} className={field} placeholder="28.00" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Material</label>
          <input value={form.material} onChange={(e) => set("material", e.target.value)} className={field} placeholder="Modal" data-testid="product-material" />
        </div>
        {!perSize && (
          <div>
            <label className="eyebrow block mb-2">Stock</label>
            <input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={field} data-testid="product-stock" />
          </div>
        )}
      </div>

      <div>
        <label className="eyebrow block mb-2">Description <span className="text-[#a8a29a] normal-case tracking-normal">optional</span></label>
        <RichTextEditor value={form.description} onChange={(html) => set("description", html)} placeholder="A refined, versatile essential… Use the toolbar to colour or restyle sentences. Blank lines are preserved." />
        <p className="text-xs text-[#737373] mt-2">Tip: select text, then pick a colour or font. Press Enter twice for a blank line — spacing is kept exactly as you type it.</p>
      </div>

      <div className="space-y-4">
        <label className="flex items-start gap-3 cursor-pointer bg-[#faf8f5] border border-[#E5E5E5] rounded-md px-4 py-3" data-testid="pre-order-toggle">
          <input type="checkbox" checked={preOrder} onChange={(e) => setPreOrder(e.target.checked)} className="mt-1" data-testid="pre-order-checkbox" />
          <span className="text-sm">
            <span className="font-medium">Show pre-order message</span>
            <span className="block text-[#737373] mt-0.5">Displays a note on the product page. Edit the wording below, or leave it blank to use the default.</span>
          </span>
        </label>
        {preOrder && (
          <div className="pl-1" data-testid="pre-order-message-field">
            <label className="eyebrow block mb-1">Pre-order message</label>
            <input value={preOrderMsg} onChange={(e) => setPreOrderMsg(e.target.value)} className={field}
              placeholder="Pre-Order items will take 2-3 weeks for delivery" data-testid="pre-order-message-input" />
          </div>
        )}

        <div className="bg-[#faf8f5] border border-[#E5E5E5] rounded-md px-4 py-3" data-testid="notices-editor">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Custom notes / badges</span>
            <button type="button" onClick={() => setNotices((prev) => [...prev, { text: "", color: NOTE_COLORS[0].value }])}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#560216] hover:underline" data-testid="add-notice">
              <Plus className="h-3.5 w-3.5" /> Add note
            </button>
          </div>
          <p className="text-xs text-[#737373] mt-1">Add coloured notes shown on the product page (e.g. "Limited edition", "Ships from Canada"). Each appears with a coloured dot.</p>
          {notices.length > 0 && (
            <div className="space-y-2 mt-3">
              {notices.map((n, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`notice-row-${i}`}>
                  <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: n.color }} />
                  <input value={n.text} onChange={(e) => setNotices((prev) => prev.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x))}
                    className={`${field} flex-1`} placeholder="Note text" data-testid={`notice-text-${i}`} />
                  <select value={n.color} onChange={(e) => setNotices((prev) => prev.map((x, idx) => idx === i ? { ...x, color: e.target.value } : x))}
                    className="border border-[#E5E5E5] rounded-md px-2 py-2.5 text-sm bg-white outline-none focus:ring-1 focus:ring-black" data-testid={`notice-color-${i}`}>
                    {NOTE_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <button type="button" onClick={() => setNotices((prev) => prev.filter((_, idx) => idx !== i))}
                    className="h-9 w-9 grid place-items-center rounded-md border border-[#E5E5E5] hover:border-[#9a3b3b] hover:text-[#9a3b3b] shrink-0" data-testid={`remove-notice-${i}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Details (dot-jot list under product info) */}
      <div>
        <label className="eyebrow block mb-1">Details</label>
        <p className="text-xs text-[#737373] mb-3">These bullet points appear under "Details" on the product page. Customise them per product (material, transparency, care, etc.).</p>
        <div className="space-y-3">
          {details.map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-[#560216] text-lg leading-none">·</span>
              <input
                value={d}
                onChange={(e) => setDetails((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))}
                className={field}
                placeholder="e.g. 100% Modal"
                data-testid={`product-detail-${i}`}
              />
              <button type="button" onClick={() => setDetails((prev) => prev.filter((_, idx) => idx !== i))} className="text-[#737373] hover:text-[#9a3b3b]" data-testid={`remove-detail-${i}`}>
                <X strokeWidth={1.5} className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setDetails((prev) => [...prev, ""])} className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#737373] hover:text-[#1A1A1A]" data-testid="add-detail">
          <Plus strokeWidth={1.5} className="h-4 w-4" /> Add detail
        </button>
      </div>

      {/* Colors */}
      <div>
        <label className="eyebrow block mb-3">Colours</label>
        <div className="space-y-3">
          {colors.map((c, i) => (
            <div key={i} className="flex items-center gap-3">
              <input type="color" value={c.hex} onChange={(e) => setColors((prev) => prev.map((x, idx) => idx === i ? { ...x, hex: e.target.value } : x))} className="h-10 w-12 rounded border border-[#E5E5E5] cursor-pointer" />
              <input value={c.name} onChange={(e) => setColors((prev) => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} className={field} placeholder="Colour name (e.g. Blush)" />
              {colors.length > 1 && (
                <button type="button" onClick={() => setColors((prev) => prev.filter((_, idx) => idx !== i))} className="text-[#737373] hover:text-[#9a3b3b]"><X strokeWidth={1.5} className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setColors((prev) => [...prev, { name: "", hex: "#D8C3A5" }])} className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#737373] hover:text-[#1A1A1A]">
          <Plus strokeWidth={1.5} className="h-4 w-4" /> Add colour
        </button>
      </div>

      {/* Sizes */}
      <div>
        <label className="eyebrow block mb-1">Sizes</label>
        <p className="text-xs text-[#737373] mb-3">Choose which sizes to offer. The size chart appears automatically when LIRA Mini or LIRA Standard is selected. Leave all unchecked for a one-size product.</p>
        <div className="flex flex-wrap gap-3">
          {["LIRA Mini", "LIRA Standard"].map((s) => (
            <button key={s} type="button" onClick={() => setSizePresets((p) => ({ ...p, [s]: !p[s] }))} data-testid={`size-toggle-${s.replace(/\s+/g, "-")}`}
              className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] border transition-all ${sizePresets[s] ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5E5] text-[#1A1A1A] hover:border-[#1A1A1A]"}`}>
              {s}
            </button>
          ))}
        </div>
        {customSizes.length > 0 && (
          <div className="space-y-3 mt-4">
            {customSizes.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <input value={s} onChange={(e) => setCustomSizes((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))} className={field} placeholder="Custom size name (e.g. LIRA Maxi)" data-testid={`custom-size-input-${i}`} />
                <button type="button" onClick={() => setCustomSizes((prev) => prev.filter((_, idx) => idx !== i))} className="text-[#737373] hover:text-[#9a3b3b]"><X strokeWidth={1.5} className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setCustomSizes((prev) => [...prev, ""])} className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#737373] hover:text-[#1A1A1A]" data-testid="add-custom-size">
          <Plus strokeWidth={1.5} className="h-4 w-4" /> Add custom size
        </button>

        {/* Per-size stock */}
        {perSize && (
          <div className="mt-6 bg-beige rounded-sm p-4" data-testid="per-size-stock">
            <p className="eyebrow mb-3">Stock per size</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {currentSizes.map((s) => (
                <div key={s}>
                  <label className="text-xs text-[#737373] mb-1 block">{s}</label>
                  <input type="number" min="0" value={sizeStock[s] ?? ""} placeholder="0"
                    onChange={(e) => setSizeStock((prev) => ({ ...prev, [s]: e.target.value }))}
                    className="w-full border border-[#E5E5E5] rounded-md px-3 py-2 text-sm bg-white outline-none focus:ring-1 focus:ring-black"
                    data-testid={`size-stock-${s.replace(/\s+/g, "-")}`} />
                </div>
              ))}
            </div>
            <p className="text-xs text-[#737373] mt-3">Total stock is the sum of all sizes. A size shows as "Sold out" on the product page when its count is 0.</p>
          </div>
        )}
      </div>

      <button type="submit" disabled={saving || uploading} className="btn-primary disabled:opacity-50" data-testid="create-product-button">
        {saving ? "Saving…" : submitLabel}
      </button>
    </form>
  );
};

const AddProductForm = ({ onCreated }) => (
  <ProductForm
    submitLabel="Publish Product"
    onSubmit={async (payload, reset) => {
      const res = await api.post("/admin/products", payload);
      toast.success(`"${res.data.name}" added to the shop.`);
      reset();
      onCreated(res.data);
    }}
  />
);

const EditProductModal = ({ product, open, onOpenChange, onSaved }) => {
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-product-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Edit “{product.name}”</DialogTitle>
        </DialogHeader>
        <ProductForm
          initial={product}
          submitLabel="Save Changes"
          onSubmit={async (payload) => {
            const res = await api.patch(`/admin/products/${product.slug}`, payload);
            toast.success("Product updated.");
            onSaved(res.data);
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
};

const ImageSlot = ({ label, url, onUpload, boxClass = "aspect-[4/5]" }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const u = await uploadImage(f);
      await onUpload(u);
      toast.success(`${label} updated.`);
    } catch {
      toast.error(`Could not update ${label}.`);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className={`relative block w-full ${boxClass} rounded-sm overflow-hidden bg-beige border border-[#E5E5E5] cursor-pointer group`} data-testid={`site-image-slot-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {url ? <img src={url} alt={label} className="h-full w-full object-cover" /> : (
        <div className="h-full w-full grid place-items-center text-[#a8a29a]"><ImageIcon strokeWidth={1} className="h-6 w-6" /></div>
      )}
      <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center text-white">
        <div className="flex flex-col items-center gap-1">
          <Upload strokeWidth={1} className="h-5 w-5" />
          <span className="text-[10px] uppercase tracking-[0.15em]">{busy ? "Uploading…" : "Change"}</span>
        </div>
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={handle} />
    </label>
  );
};

const CAT_LABELS = { "solid-modal": "Solid Modal", "print-modal": "Print Modal", "chiffon": "Chiffon", "accessories": "Accessories" };

const FONT_OPTIONS = [
  { label: "Cinzel (elegant serif)", value: '"Cinzel", serif' },
  { label: "Cinzel Decorative (ornate)", value: '"Cinzel Decorative", serif' },
  { label: "Cormorant Garamond (classic)", value: '"Cormorant Garamond", serif' },
  { label: "Times New Roman (traditional)", value: '"Times New Roman", Times, serif' },
  { label: "Outfit (modern sans)", value: '"Outfit", sans-serif' },
];

const MaintenanceToggle = () => {
  const { maintenance, refresh } = useSiteSettings();
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", { maintenance: !maintenance });
      await refresh();
      toast.success(!maintenance ? "Maintenance mode ON — visitors are blocked." : "Maintenance mode OFF — site is live.");
    } catch { toast.error("Could not update maintenance mode."); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-[#E5E5E5] rounded-sm p-5 flex items-center justify-between gap-6" data-testid="maintenance-toggle-section">
      <div>
        <h3 className="font-serif text-2xl mb-1">Maintenance Mode</h3>
        <p className="text-sm text-[#737373]">
          {maintenance ? "ON — visitors see the maintenance screen. Admin and access code still work." : "OFF — your website is live for everyone."}
        </p>
      </div>
      <button onClick={toggle} disabled={saving} data-testid="maintenance-toggle-btn"
        className={`shrink-0 px-5 py-2.5 text-xs uppercase tracking-[0.2em] rounded-sm text-white disabled:opacity-50 ${maintenance ? "bg-[#3a6b45]" : "bg-red-600"}`}>
        {saving ? "Saving…" : maintenance ? "Turn Off" : "Turn On"}
      </button>
    </section>
  );
};

const SiteImagesTab = () => {
  const { logo, footer_logo, lifestyle_banner, categories, community_images, hero, nav, refresh } = useSiteSettings();
  const field = "w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black";

  const patch = async (payload) => {
    await api.put("/admin/site-settings", payload);
    await refresh();
  };

  const [heroForm, setHeroForm] = useState(hero);
  const [navItems, setNavItems] = useState(nav || []);
  const [savingHero, setSavingHero] = useState(false);
  const [savingNav, setSavingNav] = useState(false);
  useEffect(() => { setHeroForm(hero); }, [hero]);
  useEffect(() => { setNavItems(nav || []); }, [nav]);

  const setHero = (k, v) => setHeroForm((f) => ({ ...f, [k]: v }));
  const saveHero = async () => {
    setSavingHero(true);
    try { await patch({ hero: heroForm }); toast.success("Hero updated."); }
    catch { toast.error("Could not save hero."); }
    finally { setSavingHero(false); }
  };
  const saveNav = async () => {
    const cleaned = navItems
      .map((n) => ({ label: (n.label || "").trim(), slug: (n.slug || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") }))
      .filter((n) => n.label && n.slug);
    setSavingNav(true);
    try { await patch({ nav: cleaned }); setNavItems(cleaned); toast.success("Navigation menu updated."); }
    catch { toast.error("Could not save navigation."); }
    finally { setSavingNav(false); }
  };

  const setCategory = (slug, url) => patch({ categories: { ...categories, [slug]: url } });
  const setCommunity = (idx, url) => {
    const next = [...(community_images || [])];
    while (next.length < 6) next.push("");
    next[idx] = url;
    return patch({ community_images: next });
  };

  const community = [...(community_images || [])];
  while (community.length < 6) community.push("");

  return (
    <div className="max-w-4xl space-y-14" data-testid="site-images-tab">
      <MaintenanceToggle />
      {/* HERO EDITOR */}
      <section data-testid="hero-editor">
        <h3 className="font-serif text-2xl mb-1">Homepage Hero</h3>
        <p className="text-sm text-[#737373] mb-4">The main banner at the top of your homepage — image, text, and button.</p>
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          <div>
            <p className="eyebrow mb-2">Background image</p>
            <ImageSlot label="Hero image" url={heroForm?.image} onUpload={(u) => setHero("image", u)} boxClass="aspect-[16/10]" />
          </div>
          <div className="space-y-5">
            <div>
              <label className="eyebrow block mb-1">Heading (line 1)</label>
              <input value={heroForm?.heading || ""} onChange={(e) => setHero("heading", e.target.value)} className={field} data-testid="hero-heading-input" />
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Font</label>
                  <select value={heroForm?.heading_font || ""} onChange={(e) => setHero("heading_font", e.target.value)} className={`${field} bg-white`} data-testid="hero-heading-font">
                    {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={heroForm?.heading_color || "#ffffff"} onChange={(e) => setHero("heading_color", e.target.value)} className="h-10 w-12 rounded border border-[#E5E5E5] cursor-pointer" data-testid="hero-heading-color" />
                    <input value={heroForm?.heading_color || ""} onChange={(e) => setHero("heading_color", e.target.value)} className={field} />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1">Subheading (line 2)</label>
              <input value={heroForm?.subheading || ""} onChange={(e) => setHero("subheading", e.target.value)} className={field} data-testid="hero-subheading-input" />
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Font</label>
                  <select value={heroForm?.subheading_font || ""} onChange={(e) => setHero("subheading_font", e.target.value)} className={`${field} bg-white`} data-testid="hero-subheading-font">
                    {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={heroForm?.subheading_color || "#ffffff"} onChange={(e) => setHero("subheading_color", e.target.value)} className="h-10 w-12 rounded border border-[#E5E5E5] cursor-pointer" data-testid="hero-subheading-color" />
                    <input value={heroForm?.subheading_color || ""} onChange={(e) => setHero("subheading_color", e.target.value)} className={field} />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-[#E5E5E5] pt-4">
              <label className="eyebrow block mb-1">Button</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Button text</label>
                  <input value={heroForm?.button_text || ""} onChange={(e) => setHero("button_text", e.target.value)} className={field} data-testid="hero-button-text" />
                </div>
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Links to (path or full URL)</label>
                  <input value={heroForm?.button_link || ""} onChange={(e) => setHero("button_link", e.target.value)} placeholder="/collection/all" className={field} data-testid="hero-button-link" />
                </div>
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Button colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={heroForm?.button_color || "#1A1A1A"} onChange={(e) => setHero("button_color", e.target.value)} className="h-10 w-12 rounded border border-[#E5E5E5] cursor-pointer" data-testid="hero-button-color" />
                    <input value={heroForm?.button_color || ""} onChange={(e) => setHero("button_color", e.target.value)} className={field} />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#737373] mb-1 block">Button text colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={heroForm?.button_text_color || "#ffffff"} onChange={(e) => setHero("button_text_color", e.target.value)} className="h-10 w-12 rounded border border-[#E5E5E5] cursor-pointer" data-testid="hero-button-text-color" />
                    <input value={heroForm?.button_text_color || ""} onChange={(e) => setHero("button_text_color", e.target.value)} className={field} />
                  </div>
                </div>
              </div>
            </div>
            <button onClick={saveHero} disabled={savingHero} className="btn-primary disabled:opacity-60" data-testid="save-hero">
              {savingHero ? "Saving…" : "Save Hero"}
            </button>
          </div>
        </div>
      </section>

      {/* NAV EDITOR */}
      <section data-testid="nav-editor">
        <h3 className="font-serif text-2xl mb-1">Navigation Menu</h3>
        <p className="text-sm text-[#737373] mb-4">The links in the top bar. Each links to <code>/collection/&lt;slug&gt;</code>. Use an existing category slug (solid-modal, print-modal, chiffon, accessories) or "all", or make a new one.</p>
        <div className="space-y-3 max-w-2xl">
          {navItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`nav-item-${i}`}>
              <input value={item.label} onChange={(e) => setNavItems((prev) => prev.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} placeholder="Label (e.g. Chiffon)" className={`${field} flex-1`} data-testid={`nav-label-${i}`} />
              <span className="text-[#a8a29a] text-sm">/collection/</span>
              <input value={item.slug} onChange={(e) => setNavItems((prev) => prev.map((x, idx) => idx === i ? { ...x, slug: e.target.value } : x))} placeholder="slug" className={`${field} w-40`} data-testid={`nav-slug-${i}`} />
              <button type="button" onClick={() => setNavItems((prev) => prev.filter((_, idx) => idx !== i))} className="h-9 w-9 grid place-items-center rounded-md border border-[#E5E5E5] hover:border-[#9a3b3b] hover:text-[#9a3b3b] shrink-0" data-testid={`remove-nav-${i}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-1">
            <button type="button" onClick={() => setNavItems((prev) => [...prev, { label: "", slug: "" }])} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#560216] hover:underline" data-testid="add-nav-item">
              <Plus className="h-4 w-4" /> Add menu item
            </button>
            <button onClick={saveNav} disabled={savingNav} className="btn-primary disabled:opacity-60" data-testid="save-nav">
              {savingNav ? "Saving…" : "Save Menu"}
            </button>
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-1">Logo</h3>
        <p className="text-sm text-[#737373] mb-4">Shown in the header. Use a transparent PNG for best results.</p>
        <div className="w-48 bg-white border border-[#E5E5E5] rounded-sm p-4">
          <ImageSlot label="Logo" url={logo} onUpload={(u) => patch({ logo: u })} boxClass="aspect-[3/2] bg-white" />
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-1">Footer Logo</h3>
        <p className="text-sm text-[#737373] mb-4">Shown on the deep-red footer. Upload a white/light transparent PNG so it's visible. Leave empty to automatically use your main logo turned white.</p>
        <div className="w-48 rounded-sm p-4" style={{ backgroundColor: "#560216" }}>
          <ImageSlot label="Footer Logo" url={footer_logo} onUpload={(u) => patch({ footer_logo: u })} boxClass="aspect-[3/2]" />
        </div>
        {footer_logo && (
          <button
            type="button"
            onClick={() => patch({ footer_logo: "" })}
            className="mt-3 text-xs uppercase tracking-[0.15em] text-[#737373] hover:text-[#9a3b3b] underline underline-offset-4"
            data-testid="reset-footer-logo"
          >
            Reset to auto (use main logo in white)
          </button>
        )}
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-1">Explore the Collections</h3>
        <p className="text-sm text-[#737373] mb-4">The category cards shown on the home page and in the header hover menu.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {["solid-modal", "print-modal", "chiffon", "accessories"].map((slug) => (
            <div key={slug}>
              <p className="eyebrow mb-2">{CAT_LABELS[slug]}</p>
              <ImageSlot label={CAT_LABELS[slug]} url={categories?.[slug]} onUpload={(u) => setCategory(slug, u)} />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-1">Lifestyle Banner</h3>
        <p className="text-sm text-[#737373] mb-4">The large full-width image in the middle of the home page.</p>
        <div className="max-w-2xl">
          <ImageSlot label="Lifestyle Banner" url={lifestyle_banner} onUpload={(u) => patch({ lifestyle_banner: u })} boxClass="aspect-[16/7]" />
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-1">Community Grid</h3>
        <p className="text-sm text-[#737373] mb-4">The six photos near the bottom of the home page.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {community.map((url, i) => (
            <ImageSlot key={i} label={`Community ${i + 1}`} url={url} onUpload={(u) => setCommunity(i, u)} boxClass="aspect-square" />
          ))}
        </div>
      </section>
    </div>
  );
};



export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [products, setProducts] = useState([]);
  const [prodFetching, setProdFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) { navigate("/account?next=admin"); return; }
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])).finally(() => setFetching(false));
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => setProducts([])).finally(() => setProdFetching(false));
  }, [user, loading, navigate]);

  const onSaved = (updated) => {
    setOrders((prev) => prev.map((o) => (o.order_id === updated.order_id ? updated : o)));
  };

  const onStockSaved = (updated) => {
    setProducts((prev) => prev.map((p) => (p.slug === updated.slug ? updated : p)).sort((a, b) => a.stock - b.stock));
  };

  const onProductDeleted = (slug) => {
    setProducts((prev) => prev.filter((p) => p.slug !== slug));
  };

  const onProductCreated = (product) => {
    setProducts((prev) => [...prev, product].sort((a, b) => a.stock - b.stock));
    setTab("inventory");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "lira-orders.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export orders.");
    }
  };

  if (loading || !user) return <div className="container-lira py-32 text-center text-[#737373]">Loading…</div>;

  const counts = {
    processing: orders.filter((o) => (o.fulfillment_status || "processing") === "processing").length,
    shipped: orders.filter((o) => o.fulfillment_status === "shipped").length,
    delivered: orders.filter((o) => o.fulfillment_status === "delivered").length,
  };
  const lowCount = products.filter((p) => p.stock > 0 && p.stock <= 10).length;
  const outCount = products.filter((p) => p.stock === 0).length;

  const TITLES = { orders: "Orders", inventory: "Inventory", add: "Add Product", site: "Site Images", email: "Email Campaign", sms: "Text Messages", discounts: "Discount Codes", aistudio: "AI Studio" };
  const TABS = [{ id: "orders", label: "Orders" }, { id: "inventory", label: "Inventory" }, { id: "add", label: "Add Product" }, { id: "site", label: "Site Images" }, { id: "email", label: "Email Campaign" }, { id: "sms", label: "Text Messages" }, { id: "discounts", label: "Discount Codes" }, { id: "aistudio", label: "AI Studio" }];

  return (
    <div className="container-lira py-14 md:py-20 min-h-[70vh]" data-testid="admin-page">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Store Management</p>
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">{TITLES[tab]}</h1>
        </div>
        {tab === "orders" && (
          <button onClick={exportCsv} className="btn-secondary" data-testid="export-csv-button">
            <Download strokeWidth={1} className="h-4 w-4 mr-2" /> Export CSV
          </button>
        )}
      </div>

      <div className="flex gap-8 border-b border-[#E5E5E5] mb-10">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`pb-3 text-xs uppercase tracking-[0.2em] transition-colors ${tab === t.id ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A]" : "text-[#737373]"}`}>
            {t.label}{t.id === "inventory" && (lowCount + outCount) > 0 ? ` (${lowCount + outCount})` : ""}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-10 max-w-xl">
            <div className="border border-[#E5E5E5] rounded-sm p-5 flex flex-col items-center gap-2"><Package strokeWidth={1} className="h-5 w-5" /><span className="text-2xl font-serif">{counts.processing}</span><span className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Processing</span></div>
            <div className="border border-[#E5E5E5] rounded-sm p-5 flex flex-col items-center gap-2"><Truck strokeWidth={1} className="h-5 w-5" /><span className="text-2xl font-serif">{counts.shipped}</span><span className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Shipped</span></div>
            <div className="border border-[#E5E5E5] rounded-sm p-5 flex flex-col items-center gap-2"><CheckCircle2 strokeWidth={1} className="h-5 w-5" /><span className="text-2xl font-serif">{counts.delivered}</span><span className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Delivered</span></div>
          </div>
          {fetching ? (
            <p className="text-[#737373]">Loading orders…</p>
          ) : orders.length === 0 ? (
            <div className="border border-[#E5E5E5] rounded-sm p-12 text-center text-[#737373]">No paid orders yet.</div>
          ) : (
            <div className="space-y-5">
              {orders.map((o) => <OrderRow key={o.order_id} order={o} onSaved={onSaved} />)}
            </div>
          )}
        </>
      )}

      {tab === "inventory" && (
        prodFetching ? (
          <p className="text-[#737373]">Loading products…</p>
        ) : (
          <div className="space-y-3">
            {products.map((p) => <InventoryRow key={p.slug} product={p} onSaved={onStockSaved} onDeleted={onProductDeleted} />)}
          </div>
        )
      )}

      {tab === "add" && <AddProductForm onCreated={onProductCreated} />}

      {tab === "site" && <SiteImagesTab />}

      {tab === "email" && <EmailCampaignTab />}

      {tab === "sms" && <SmsCampaignTab />}

      {tab === "discounts" && <DiscountsTab />}

      {tab === "aistudio" && <AIStudioTab />}
    </div>
  );
}
